import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BanknotesIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [formData, setFormData] = useState({});
  const [feeSettings, setFeeSettings] = useState({
    transactionFee: 2.5,
    withdrawalFee: 1.0,
    minimumWithdrawal: 10,
    feeThreshold: 100,
    referralEnabled: true,
    referralPercent: 5,
    referralLevels: 2,
    minimumReferralPayout: 5,
    autoReleaseReferrals: true,
    referralExpiryDays: 30,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("telegram");

  // Subscription management states
  const [subscriptionPackages, setSubscriptionPackages] = useState([]);
  const [isAddingPackage, setIsAddingPackage] = useState(false);
  const [isEditingPackage, setIsEditingPackage] = useState(false);
  const [currentPackageId, setCurrentPackageId] = useState(null);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showExpirationSettings, setShowExpirationSettings] = useState(false);
  const [packageForm, setPackageForm] = useState({
    name: "",
    monthlyPrice: "",
    monthlyDiscount: "",
    yearlyPrice: "",
    yearlyDiscount: "",
    features: [],
    isPopular: false,
    color: "#3b82f6", // Default color - blue
    isActive: true,
  });

  // Expiration settings
  const [expirationSettings, setExpirationSettings] = useState({
    sendReminderDaysBefore: 7,
    remindersEnabled: true,
    gracePeriodDays: 3,
    autoRenewDefault: true,
    allowCancellation: true,
    cancellationNoticeDays: 14,
    sendExpirationEmail: true,
    expirationEmailTemplate:
      "Your subscription is about to expire. Renew now to maintain access to all features.",
  });

  useEffect(() => {
    fetchSettings();
    fetchSubscriptionPackages();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/admin/settings/", {
        withCredentials: true,
      });
      setSettings(response.data);
      setFormData(response.data);
      setError("");
    } catch (err) {
      console.error("Failed to fetch settings:", err);
      setError("Failed to load settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionPackages = async () => {
    try {
      // In a real app, this would be an API call
      // const response = await axios.get('/api/admin/subscription-packages');
      // setSubscriptionPackages(response.data);

      // Mock data for demonstration
      setTimeout(() => {
        const mockPackages = [
          {
            id: 1,
            name: "Starter",
            monthlyPrice: 49.99,
            monthlyDiscount: 0,
            yearlyPrice: 499.99,
            yearlyDiscount: 99.99,
            features: [
              "Basic trading bots",
              "Up to 3 active bots",
              "Email support",
            ],
            isPopular: false,
            color: "#10b981", // green
          },
          {
            id: 2,
            name: "Professional",
            monthlyPrice: 149.99,
            monthlyDiscount: 20,
            yearlyPrice: 1499.99,
            yearlyDiscount: 299.99,
            features: [
              "All trading bots",
              "Up to 10 active bots",
              "Priority email support",
              "Phone support",
              "Advanced analytics",
            ],
            isPopular: true,
            color: "#3b82f6", // blue
          },
          {
            id: 3,
            name: "Enterprise",
            monthlyPrice: 299.99,
            monthlyDiscount: 0,
            yearlyPrice: 2999.99,
            yearlyDiscount: 599.99,
            features: [
              "Unlimited trading bots",
              "Custom bot development",
              "Dedicated account manager",
              "24/7 phone and email support",
              "Advanced analytics",
              "API access",
            ],
            isPopular: false,
            color: "#8b5cf6", // purple
          },
        ];
        setSubscriptionPackages(mockPackages);
      }, 500);
    } catch (err) {
      console.error("Failed to fetch subscription packages:", err);
    }
  };

  const handleAddPackage = () => {
    setIsAddingPackage(true);
    setIsEditingPackage(false);
    setCurrentPackageId(null);
    setPackageForm({
      name: "",
      monthlyPrice: "",
      monthlyDiscount: "",
      yearlyPrice: "",
      yearlyDiscount: "",
      features: [],
      isPopular: false,
      color: "#3b82f6", // Default color - blue
    });
  };

  const handleEditPackage = (packageId) => {
    const packageToEdit = subscriptionPackages.find(
      (pkg) => pkg.id === packageId,
    );
    if (packageToEdit) {
      setIsEditingPackage(true);
      setIsAddingPackage(false);
      setCurrentPackageId(packageId);
      setPackageForm({
        ...packageToEdit,
        features: [...packageToEdit.features],
      });
    }
  };

  const handlePackageFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    setPackageForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...packageForm.features];
    updatedFeatures[index] = value;

    setPackageForm((prev) => ({
      ...prev,
      features: updatedFeatures,
    }));
  };

  const addFeatureField = () => {
    setPackageForm((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const removeFeatureField = (index) => {
    const updatedFeatures = [...packageForm.features];
    updatedFeatures.splice(index, 1);

    setPackageForm((prev) => ({
      ...prev,
      features: updatedFeatures,
    }));
  };

  const savePackage = () => {
    // Form validation
    if (
      !packageForm.name ||
      packageForm.monthlyPrice <= 0 ||
      packageForm.yearlyPrice <= 0
    ) {
      setError(
        "Please fill in all required fields (name, monthly price, yearly price)",
      );
      setTimeout(() => setError(""), 3000);
      return;
    }
    if (isEditingPackage) {
      // In a real app, this would be an API call to update
      // await axios.put(`/api/admin/subscription-packages/${currentPackageId}`, packageForm);

      // Update locally for demonstration
      const updatedPackages = subscriptionPackages.map((pkg) =>
        pkg.id === currentPackageId
          ? { ...packageForm, id: currentPackageId }
          : pkg,
      );
      setSubscriptionPackages(updatedPackages);
      setSuccess("Package updated successfully!");
    } else {
      // In a real app, this would be an API call to create
      // const response = await axios.post('/api/admin/subscription-packages', packageForm);
      // const newPackage = response.data;

      // Create locally for demonstration
      const newId = Math.max(...subscriptionPackages.map((p) => p.id), 0) + 1;
      const newPackage = {
        ...packageForm,
        id: newId,
      };
      setSubscriptionPackages([...subscriptionPackages, newPackage]);
      setSuccess("Package created successfully!");
    }

    setIsAddingPackage(false);
    setIsEditingPackage(false);
    setCurrentPackageId(null);

    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(""), 3000);
  };

  const deletePackage = (id) => {
    // Using window.confirm instead of direct confirm to avoid ESLint error
    if (window.confirm("Are you sure you want to delete this package?")) {
      const newPackages = subscriptionPackages.filter((pkg) => pkg.id !== id);
      setSubscriptionPackages(newPackages);
      setSuccess("Package deleted successfully!");
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested properties using dot notation (e.g. "bot.max_active")
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]:
            type === "checkbox"
              ? checked
              : type === "number"
              ? parseFloat(value)
              : value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
            ? parseFloat(value)
            : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post("/api/admin/settings/update/", formData, {
        withCredentials: true,
      });
      setSuccess("Settings updated successfully!");
      setTimeout(() => setSuccess(""), 3000);
      fetchSettings(); // Refresh settings
    } catch (err) {
      console.error("Failed to update settings:", err);
      setError("Failed to update settings. Please try again.");
    }
  };

  const renderGeneralSettings = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Site Name
          </label>
          <input
            type="text"
            name="site_name"
            value={formData.site_name || ""}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Support Email
          </label>
          <input
            type="email"
            name="support_email"
            value={formData.support_email || ""}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Maintenance Mode
          </label>
          <div className="flex items-center">
            <input
              type="checkbox"
              name="maintenance_mode"
              checked={formData.maintenance_mode || false}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Enable Maintenance Mode
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            User Registrations
          </label>
          <div className="flex items-center">
            <input
              type="checkbox"
              name="allow_registrations"
              checked={formData.allow_registrations || false}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Allow New User Registrations
            </span>
          </div>
        </div>
      </div>
    </>
  );

  const renderTelegramBotSettings = () => (
    <>
      <div className="mb-6 border-b pb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3">
          Telegram Bot Configuration
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure the Xenorize Telegram bot to allow users to run their bots
          using their phone numbers. All user interactions will be through the
          official Xenorize Bot Father account.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Telegram Bot Token
          </label>
          <input
            type="password"
            name="telegram.bot_token"
            value={formData.telegram?.bot_token || ""}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="1234567890:ABCDEF-ghijklmnopqrstuvwxyz"
          />
          <p className="mt-1 text-xs text-gray-500">
            The Bot API token provided by @BotFather
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Webhook URL
          </label>
          <input
            type="text"
            name="telegram.webhook_url"
            value={formData.telegram?.webhook_url || ""}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="https://xenorize.com/api/telegram-webhook"
          />
          <p className="mt-1 text-xs text-gray-500">
            URL where Telegram will send updates to your bot
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bot Username
          </label>
          <div className="flex items-center">
            <span className="text-gray-500 mr-1">@</span>
            <input
              type="text"
              name="telegram.bot_username"
              value={formData.telegram?.bot_username || ""}
              onChange={handleInputChange}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              placeholder="XenorizeBot"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            The username of your Telegram bot (without @)
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Enable User Authentication
          </label>
          <div className="flex items-center">
            <input
              type="checkbox"
              name="telegram.user_auth_enabled"
              checked={formData.telegram?.user_auth_enabled || false}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Require users to authenticate with Xenorize account
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Enable Bot Commands
          </label>
          <div className="flex items-center">
            <input
              type="checkbox"
              name="telegram.commands_enabled"
              checked={formData.telegram?.commands_enabled || true}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Enable standard bot commands
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Welcome Message
          </label>
          <textarea
            name="telegram.welcome_message"
            value={
              formData.telegram?.welcome_message ||
              "Welcome to the Xenorize Bot! Your phone number is now connected to your Xenorize account."
            }
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
          <p className="mt-1 text-xs text-gray-500">
            Message sent to users when they first connect their phone number
          </p>
        </div>
      </div>
    </>
  );

  // Handle fee settings change
  const handleFeeSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFeeSettings((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? parseFloat(value)
          : value,
    }));
  };

  const renderFeeSettings = () => (
    <>
      <div className="mb-6 border-b pb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3">
          Transaction Fee Settings
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure transaction fees, withdrawal fees, and minimum thresholds
          for the platform.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Transaction Fee (%)
          </label>
          <input
            type="number"
            name="transactionFee"
            value={feeSettings.transactionFee}
            onChange={handleFeeSettingsChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min="0"
            step="0.1"
          />
          <p className="mt-1 text-xs text-gray-500">
            Fee applied to all transactions on the platform
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Withdrawal Fee (%)
          </label>
          <input
            type="number"
            name="withdrawalFee"
            value={feeSettings.withdrawalFee}
            onChange={handleFeeSettingsChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min="0"
            step="0.1"
          />
          <p className="mt-1 text-xs text-gray-500">
            Fee applied when users withdraw funds
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Minimum Withdrawal ($)
          </label>
          <input
            type="number"
            name="minimumWithdrawal"
            value={feeSettings.minimumWithdrawal}
            onChange={handleFeeSettingsChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min="0"
            step="0.01"
          />
          <p className="mt-1 text-xs text-gray-500">
            Minimum amount required for withdrawal
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fee Threshold ($)
          </label>
          <input
            type="number"
            name="feeThreshold"
            value={feeSettings.feeThreshold}
            onChange={handleFeeSettingsChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min="0"
            step="0.01"
          />
          <p className="mt-1 text-xs text-gray-500">
            Transactions above this amount may have reduced fees
          </p>
        </div>
      </div>

      <div className="mb-6 border-b pb-4 mt-8">
        <h3 className="text-lg font-medium text-gray-900 mb-3">
          Referral Program Settings
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure the referral program including commission rates, levels, and
          payout rules.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Referral Program
          </label>
          <div className="flex items-center">
            <input
              type="checkbox"
              name="referralEnabled"
              checked={feeSettings.referralEnabled}
              onChange={handleFeeSettingsChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Enable referral program
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Referral Commission (%)
          </label>
          <input
            type="number"
            name="referralPercent"
            value={feeSettings.referralPercent}
            onChange={handleFeeSettingsChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min="0"
            max="100"
            step="0.1"
            disabled={!feeSettings.referralEnabled}
          />
          <p className="mt-1 text-xs text-gray-500">
            Percentage commission earned by referrers
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Referral Levels
          </label>
          <input
            type="number"
            name="referralLevels"
            value={feeSettings.referralLevels}
            onChange={handleFeeSettingsChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min="1"
            max="5"
            step="1"
            disabled={!feeSettings.referralEnabled}
          />
          <p className="mt-1 text-xs text-gray-500">
            Number of levels in the referral hierarchy
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Minimum Referral Payout ($)
          </label>
          <input
            type="number"
            name="minimumReferralPayout"
            value={feeSettings.minimumReferralPayout}
            onChange={handleFeeSettingsChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min="0"
            step="0.01"
            disabled={!feeSettings.referralEnabled}
          />
          <p className="mt-1 text-xs text-gray-500">
            Minimum amount required for referral payout
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Auto-Release Referral Earnings
          </label>
          <div className="flex items-center">
            <input
              type="checkbox"
              name="autoReleaseReferrals"
              checked={feeSettings.autoReleaseReferrals}
              onChange={handleFeeSettingsChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={!feeSettings.referralEnabled}
            />
            <span className="ml-2 text-sm text-gray-700">
              Automatically release referral earnings to user balance
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Referral Expiry (days)
          </label>
          <input
            type="number"
            name="referralExpiryDays"
            value={feeSettings.referralExpiryDays}
            onChange={handleFeeSettingsChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min="0"
            step="1"
            disabled={!feeSettings.referralEnabled}
          />
          <p className="mt-1 text-xs text-gray-500">
            Number of days until referral link expires (0 = never)
          </p>
        </div>
      </div>
    </>
  );

  // Handle expiration settings form change
  const handleExpirationSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExpirationSettings((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? parseInt(value, 10)
          : value,
    }));
  };

  // Handle bulk actions
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedPackages([]);
    } else {
      setSelectedPackages(subscriptionPackages.map((pkg) => pkg.id));
    }
    setSelectAll(!selectAll);
  };

  const togglePackageSelection = (packageId) => {
    if (selectedPackages.includes(packageId)) {
      setSelectedPackages(selectedPackages.filter((id) => id !== packageId));
      setSelectAll(false);
    } else {
      setSelectedPackages([...selectedPackages, packageId]);
      if (selectedPackages.length + 1 === subscriptionPackages.length) {
        setSelectAll(true);
      }
    }
  };

  const bulkDeletePackages = () => {
    if (selectedPackages.length === 0) return;

    // Using window.confirm instead of direct confirm to avoid ESLint error
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedPackages.length} selected package(s)?`,
      )
    ) {
      // In a real app, this would be an API call to bulk delete
      // await axios.delete('/api/admin/subscription-packages/bulk', { data: { ids: selectedPackages } });

      // Delete locally for demonstration
      const filteredPackages = subscriptionPackages.filter(
        (pkg) => !selectedPackages.includes(pkg.id),
      );
      setSubscriptionPackages(filteredPackages);
      setSelectedPackages([]);
      setSelectAll(false);
      setSuccess(`${selectedPackages.length} package(s) deleted successfully!`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  const bulkTogglePackages = (activate = true) => {
    if (selectedPackages.length === 0) return;

    // In a real app, this would be an API call to bulk update
    // await axios.put('/api/admin/subscription-packages/bulk', { ids: selectedPackages, isActive: activate });

    // Update locally for demonstration
    const updatedPackages = subscriptionPackages.map((pkg) => {
      if (selectedPackages.includes(pkg.id)) {
        return { ...pkg, isActive: activate };
      }
      return pkg;
    });

    setSubscriptionPackages(updatedPackages);
    setSuccess(
      `${selectedPackages.length} package(s) ${
        activate ? "activated" : "deactivated"
      } successfully!`,
    );

    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(""), 3000);
  };

  const renderSubscriptionSettings = () => (
    <>
      <div className="mb-6 border-b pb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3">
          Subscription Package Management
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure the subscription packages available to users. Set monthly
          and yearly pricing, discounts, and features for each package level.
        </p>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleAddPackage}
            disabled={isAddingPackage || isEditingPackage}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add New Package
          </button>

          <button
            onClick={() => setShowExpirationSettings(!showExpirationSettings)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md flex items-center"
          >
            {showExpirationSettings ? (
              <>
                <ChevronUpIcon className="h-4 w-4 mr-1" />
                Hide Expiration Settings
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-4 w-4 mr-1" />
                Show Expiration Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* Subscription Expiration Settings */}
      {showExpirationSettings && (
        <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            Subscription Expiration Settings
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Send Reminder (days before expiration)
              </label>
              <input
                type="number"
                name="sendReminderDaysBefore"
                value={expirationSettings.sendReminderDaysBefore}
                onChange={handleExpirationSettingsChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reminders
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="remindersEnabled"
                  checked={expirationSettings.remindersEnabled}
                  onChange={handleExpirationSettingsChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Enable expiration reminders
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grace Period (days after expiration)
              </label>
              <input
                type="number"
                name="gracePeriodDays"
                value={expirationSettings.gracePeriodDays}
                onChange={handleExpirationSettingsChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auto-Renew
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="autoRenewDefault"
                  checked={expirationSettings.autoRenewDefault}
                  onChange={handleExpirationSettingsChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Set auto-renew as default
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cancellation Notice (days)
              </label>
              <input
                type="number"
                name="cancellationNoticeDays"
                value={expirationSettings.cancellationNoticeDays}
                onChange={handleExpirationSettingsChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cancellation
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="allowCancellation"
                  checked={expirationSettings.allowCancellation}
                  onChange={handleExpirationSettingsChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Allow users to cancel subscriptions
                </span>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiration Email
            </label>
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                name="sendExpirationEmail"
                checked={expirationSettings.sendExpirationEmail}
                onChange={handleExpirationSettingsChange}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Send expiration email notification
              </span>
            </div>
            <textarea
              name="expirationEmailTemplate"
              value={expirationSettings.expirationEmailTemplate}
              onChange={handleExpirationSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows="3"
              placeholder="Template for expiration notification email"
              disabled={!expirationSettings.sendExpirationEmail}
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowExpirationSettings(false)}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-3 py-1 rounded-md"
            >
              Close
            </button>
            <button
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md"
              onClick={() =>
                setSuccess("Expiration settings saved successfully!")
              }
            >
              Save Expiration Settings
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Package Form */}
      {(isAddingPackage || isEditingPackage) && (
        <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            {isEditingPackage ? "Edit Package" : "Add New Package"}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Package Name*
              </label>
              <input
                type="text"
                name="name"
                value={packageForm.name}
                onChange={handlePackageFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <input
                type="color"
                name="color"
                value={packageForm.color}
                onChange={handlePackageFormChange}
                className="h-10 p-1 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Price ($)*
              </label>
              <input
                type="number"
                name="monthlyPrice"
                value={packageForm.monthlyPrice}
                onChange={handlePackageFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                step="0.01"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Discount ($)
              </label>
              <input
                type="number"
                name="monthlyDiscount"
                value={packageForm.monthlyDiscount}
                onChange={handlePackageFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yearly Price ($)*
              </label>
              <input
                type="number"
                name="yearlyPrice"
                value={packageForm.yearlyPrice}
                onChange={handlePackageFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                step="0.01"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yearly Discount ($)
              </label>
              <input
                type="number"
                name="yearlyDiscount"
                value={packageForm.yearlyDiscount}
                onChange={handlePackageFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Features
              </label>
              <button
                type="button"
                onClick={addFeatureField}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <PlusIcon className="h-3 w-3 mr-1" />
                Add Feature
              </button>
            </div>

            {packageForm.features.map((feature, index) => (
              <div key={index} className="flex items-center mb-2">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => handleFeatureChange(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md mr-2"
                  placeholder={`Feature ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeFeatureField(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              name="isPopular"
              checked={packageForm.isPopular}
              onChange={handlePackageFormChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Mark as popular (highlighted)
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setIsAddingPackage(false);
                setIsEditingPackage(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={savePackage}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md"
            >
              {isEditingPackage ? "Update Package" : "Create Package"}
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {subscriptionPackages.length > 0 &&
        !isAddingPackage &&
        !isEditingPackage && (
          <div className="mb-6 p-3 bg-gray-100 rounded-md">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center mr-4">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Select All (
                  {selectAll
                    ? subscriptionPackages.length
                    : selectedPackages.length}
                  /{subscriptionPackages.length})
                </span>
              </div>

              {selectedPackages.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => bulkDeletePackages()}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md flex items-center text-sm"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Delete Selected
                  </button>
                  <button
                    onClick={() => bulkTogglePackages(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md flex items-center text-sm"
                  >
                    Activate Selected
                  </button>
                  <button
                    onClick={() => bulkTogglePackages(false)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-md flex items-center text-sm"
                  >
                    Deactivate Selected
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Packages List */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-gray-900 mb-3">
          Current Packages
        </h4>

        {subscriptionPackages.length === 0 ? (
          <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-md">
            No subscription packages found. Click "Add New Package" to create
            one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subscriptionPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`border rounded-lg overflow-hidden ${
                  pkg.isPopular ? "ring-2 ring-blue-500" : ""
                } ${!pkg.isActive ? "opacity-60" : ""}`}
                style={{ borderColor: pkg.color }}
              >
                <div
                  className="p-4 flex justify-between items-start"
                  style={{ backgroundColor: `${pkg.color}15` }} // Very light version of the color
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPackages.includes(pkg.id)}
                      onChange={() => togglePackageSelection(pkg.id)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                    />
                  </div>
                  <div>
                    <h5
                      className="text-lg font-bold"
                      style={{ color: pkg.color }}
                    >
                      {pkg.name}
                      {pkg.isActive === false && (
                        <span className="ml-2 text-xs font-normal bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </h5>
                    {pkg.isPopular && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                        Popular Choice
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditPackage(pkg.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white p-1 rounded-md"
                      title="Edit package"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deletePackage(pkg.id)}
                      className="bg-red-600 hover:bg-red-700 text-white p-1 rounded-md"
                      title="Delete package"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="p-4 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <p className="text-gray-500 text-sm">Monthly</p>
                      <div className="flex items-end">
                        <span className="text-2xl font-bold">
                          ${pkg.monthlyPrice}
                        </span>
                        {pkg.monthlyDiscount > 0 && (
                          <span className="text-sm text-red-600 ml-2 mb-1">
                            Save ${pkg.monthlyDiscount}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-500 text-sm">Yearly</p>
                      <div className="flex items-end">
                        <span className="text-2xl font-bold">
                          ${pkg.yearlyPrice}
                        </span>
                        {pkg.yearlyDiscount > 0 && (
                          <span className="text-sm text-red-600 ml-2 mb-1">
                            Save ${pkg.yearlyDiscount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-3 mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Features:
                    </p>
                    <ul className="space-y-1">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
                          <span className="text-sm text-gray-600">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="p-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          System Settings
        </h2>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">System Settings</h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-4 flex items-center">
          <ExclamationCircleIcon className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4 mb-4 flex items-center">
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      <div className="bg-white shadow-md rounded-md overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {/* <button
              onClick={() => setActiveTab('general')}
              className={`py-4 px-6 text-sm font-medium ${
                activeTab === 'general'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              General
            </button> */}
            <button
              onClick={() => setActiveTab("telegram")}
              className={`py-4 px-6 text-sm font-medium ${
                activeTab === "telegram"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Telegram Bot
            </button>
            <button
              onClick={() => setActiveTab("fees")}
              className={`py-4 px-6 text-sm font-medium flex items-center ${
                activeTab === "fees"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <BanknotesIcon className="h-4 w-4 mr-1" />
              Fee Settings
            </button>
            <button
              onClick={() => setActiveTab("subscriptions")}
              className={`py-4 px-6 text-sm font-medium flex items-center ${
                activeTab === "subscriptions"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <CurrencyDollarIcon className="h-4 w-4 mr-1" />
              Subscriptions
            </button>
          </nav>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit}>
            {/* {activeTab === "general" && renderGeneralSettings()} */}
            {activeTab === "telegram" && renderTelegramBotSettings()}
            {activeTab === "fees" && renderFeeSettings()}
            {activeTab === "subscriptions" && renderSubscriptionSettings()}

            {activeTab !== "subscriptions" && (
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={fetchSettings}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md mr-3"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md"
                >
                  Save Changes
                </button>
              </div>
            )}
            {activeTab === "subscriptions" &&
              !isAddingPackage &&
              !isEditingPackage && (
                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setSuccess("Subscription settings saved successfully!");
                      setTimeout(() => setSuccess(""), 3000);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md flex items-center mr-2"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={fetchSubscriptionPackages}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-md flex items-center"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-1" />
                    Refresh Packages
                  </button>
                </div>
              )}
          </form>
        </div>
      </div>
    </div>
  );
}
